import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import CodeInput from "../components/CodeInput";

// ── Page principale ──────────────────────────────────────────────────────────
export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register, sendVerificationEmail, verifyEmail, forgotPassword, resetPassword } =
    useAuth();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // "auth" | "verify-email" | "forgot-email" | "forgot-code" | "forgot-newpwd"
  const [view, setView] = useState("auth");

  // Email en cours (partagé entre les vues)
  const [activeEmail, setActiveEmail] = useState("");

  // ── Données formulaires ───────────────────────────────────────────────────
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "", password: "", first_name: "", last_name: "", phone: "", referral_code: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // ── Login ─────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      // Si l'email n'est pas encore vérifié → envoyer un code et afficher la vue de vérification
      if (!userData?.is_email_verified) {
        setActiveEmail(loginData.email.toLowerCase());
        setVerifyCode("");
        await sendVerificationEmail(loginData.email.toLowerCase());
        toast.info("Vérifiez votre email avant de continuer.");
        setView("verify-email");
        return;
      }
      toast.success("Connexion réussie !");
      const isAdmin = userData?.roles?.some((r) => r.role_type === "ADMIN");
      navigate(isAdmin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  // ── Register ──────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== confirmPassword) {
      return toast.error("Les mots de passe ne correspondent pas");
    }
    setLoading(true);
    try {
      await register(registerData);
      toast.success("Compte créé ! Vérifiez votre email.");
      setActiveEmail(registerData.email.toLowerCase());
      setVerifyCode("");
      setView("verify-email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  // ── Vérification email ────────────────────────────────────────────────────
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (verifyCode.length < 6) return toast.error("Entrez les 6 chiffres du code");
    setLoading(true);
    try {
      const result = await verifyEmail(activeEmail, verifyCode);
      toast.success("Email vérifié ! Bienvenue !");
      const isAdmin = result?.user?.roles?.some((r) => r.role_type === "ADMIN");
      navigate(isAdmin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendVerificationEmail(activeEmail);
      toast.success("Nouveau code envoyé !");
    } catch {
      toast.error("Impossible de renvoyer le code");
    }
  };

  // ── Mot de passe oublié ───────────────────────────────────────────────────
  const handleForgotSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success("Si cet email existe, un code a été envoyé.");
      setActiveEmail(forgotEmail.toLowerCase());
      setResetCode("");
      setView("forgot-code");
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetCode.length < 6) return toast.error("Entrez les 6 chiffres du code");
    if (newPassword.length < 6) return toast.error("Le mot de passe doit faire au moins 6 caractères");
    setLoading(true);
    try {
      await resetPassword(activeEmail, resetCode, newPassword);
      toast.success("Mot de passe réinitialisé ! Connectez-vous.");
      setView("auth");
      setLoginData({ email: activeEmail, password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour à l'accueil
        </Link>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <CardTitle className="font-heading text-2xl">Marketplace</CardTitle>
            <CardDescription>
              {view === "verify-email" && "Vérifiez votre adresse email"}
              {view === "forgot-email" && "Réinitialiser le mot de passe"}
              {view === "forgot-code" && "Entrez le code reçu"}
              {view === "auth" && "Connectez-vous ou créez un compte"}
            </CardDescription>
          </CardHeader>

          <CardContent>

            {/* ── Vue : Login / Register ─────────────────────────────────── */}
            {view === "auth" && (
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
                </TabsList>

                {/* Login */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Lien mot de passe oublié */}
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-sm text-orange-500 hover:text-orange-600 hover:underline"
                        onClick={() => { setForgotEmail(loginData.email); setView("forgot-email"); }}
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                      {loading ? "Connexion..." : "Se connecter"}
                    </Button>
                  </form>
                </TabsContent>

                {/* Register */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-firstname">Prénom</Label>
                        <Input
                          id="register-firstname"
                          placeholder="Jean"
                          value={registerData.first_name}
                          onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                          required
                          data-testid="register-firstname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-lastname">Nom</Label>
                        <Input
                          id="register-lastname"
                          placeholder="Dupont"
                          value={registerData.last_name}
                          onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                          required
                          data-testid="register-lastname"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">Téléphone (optionnel)</Label>
                      <Input
                        id="register-phone"
                        type="tel"
                        placeholder="+509 3000 0000"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        data-testid="register-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          minLength={6}
                          data-testid="register-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm-password">Confirmer le mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="register-confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={6}
                          className={confirmPassword && confirmPassword !== registerData.password ? "border-red-400 focus-visible:ring-red-400" : ""}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {confirmPassword && confirmPassword !== registerData.password && (
                        <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-referral">Code de parrainage (optionnel)</Label>
                      <Input
                        id="register-referral"
                        placeholder="CODE123"
                        value={registerData.referral_code}
                        onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value })}
                        data-testid="register-referral"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || (confirmPassword.length > 0 && confirmPassword !== registerData.password)}
                      data-testid="register-submit"
                    >
                      {loading ? "Création..." : "Créer un compte"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}

            {/* ── Vue : Vérification email (après inscription) ───────────── */}
            {view === "verify-email" && (
              <div>
                <div className="flex flex-col items-center text-center mb-4">
                  <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                    <Mail className="h-7 w-7 text-orange-500" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Un code à 6 chiffres a été envoyé à<br />
                    <strong className="text-foreground">{activeEmail}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerifyEmail}>
                  <CodeInput value={verifyCode} onChange={setVerifyCode} />
                  <Button type="submit" className="w-full mt-2" disabled={loading || verifyCode.length < 6}>
                    {loading ? "Vérification..." : "Confirmer mon email"}
                  </Button>
                </form>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground"
                    onClick={handleResendVerification}
                  >
                    Renvoyer le code
                  </button>
                </div>
              </div>
            )}

            {/* ── Vue : Mot de passe oublié – saisie email ──────────────── */}
            {view === "forgot-email" && (
              <div>
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                    <KeyRound className="h-7 w-7 text-blue-500" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Entrez votre email pour recevoir un code de réinitialisation.
                  </p>
                </div>

                <form onSubmit={handleForgotSendCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Envoi..." : "Envoyer le code"}
                  </Button>
                </form>

                <button
                  type="button"
                  className="mt-4 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                  onClick={() => setView("auth")}
                >
                  <ArrowLeft className="h-3 w-3" /> Retour à la connexion
                </button>
              </div>
            )}

            {/* ── Vue : Code reçu + nouveau mot de passe ────────────────── */}
            {view === "forgot-code" && (
              <div>
                <div className="flex flex-col items-center text-center mb-4">
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 className="h-7 w-7 text-green-500" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Code envoyé à <strong className="text-foreground">{activeEmail}</strong>
                  </p>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-center block mb-1">Code à 6 chiffres</Label>
                    <CodeInput value={resetCode} onChange={setResetCode} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || resetCode.length < 6 || newPassword.length < 6}
                  >
                    {loading ? "Réinitialisation..." : "Changer le mot de passe"}
                  </Button>
                </form>

                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground"
                    onClick={handleForgotSendCode}
                  >
                    Renvoyer le code
                  </button>
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={() => setView("auth")}
                  >
                    <ArrowLeft className="h-3 w-3" /> Connexion
                  </button>
                </div>
              </div>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
